import { useState } from "react";
import { Button } from "@/components/ui/button";
import { m } from "@/paraglide/messages";
import type { Thumbnail } from "../lib/youtube";

/**
 * One resolution: preview image (hidden if the video has no thumbnail at this
 * size), a same-origin download button, and a copy-URL button.
 */
export function ThumbnailCard({
  id,
  thumb,
  highlighted = false,
}: {
  id: string;
  thumb: Thumbnail;
  highlighted?: boolean;
}) {
  const [available, setAvailable] = useState(true);
  const [copied, setCopied] = useState(false);

  if (!available) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(thumb.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable — no-op.
    }
  };

  return (
    <div
      className="pct-panel overflow-hidden rounded-xl"
      style={highlighted ? { borderColor: "var(--primary)" } : undefined}
    >
      <img
        src={thumb.url}
        alt={`${thumb.width}×${thumb.height} thumbnail`}
        width={thumb.width}
        height={thumb.height}
        loading="lazy"
        onError={() => setAvailable(false)}
        className="aspect-video w-full bg-muted object-cover"
      />
      <div className="flex items-center justify-between gap-2 p-3">
        <span className="font-mono text-sm tabular-nums">
          {thumb.width}×{thumb.height}
        </span>
        <div className="flex gap-2">
          <Button asChild size="sm">
            <a href={`/api/youtube-thumbnail?id=${id}&q=${thumb.key}`}>
              {m.yt_download()}
            </a>
          </Button>
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? m.yt_copied() : m.yt_copy()}
          </Button>
        </div>
      </div>
    </div>
  );
}
